import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import MutationQueueLink from "@adobe/apollo-link-mutation-queue";
import { ApolloLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import React from "react";
import ReactDOM from "react-dom";
import { browserTracingIntegration, init, Replay } from "@sentry/react";
import "./index.scss";
import { Adapter } from "@magento/venia-drivers";
import store from "./store";
import app from "@magento/peregrine/lib/store/actions/app";
import App from "./App";
import AppContextProvider from "./contextProvider";
import BrowserPersistence from "@overrides_venia/peregrine/util/simplePersistence";
import { registerSW } from "./registerSW";
import { env } from "build/env";
var apiBase = process.env.GRAPHQL_ENDPOINT;
init({
  dsn: process.env.SENTRY_DSN,
  environment: process.env.SESSION_TRACKING_ENV,
  release: process.env.SENTRY_RELEASE,
  integrations: [browserTracingIntegration({
    beforeStartSpan: context => {
      var path = location.pathname;
      if (path.endsWith('/') && path.length > 1) {
        path = path.slice(0, -1);
      }
      if (path.startsWith('/it/') || path.startsWith('/en/')) {
        path = path.substring(3);
      } else if (path === '/it' || path === '/en') {
        path = '/';
      }
      return _objectSpread(_objectSpread({}, context), {}, {
        name: path
      });
    }
  }), new Replay({
    maskAllText: false,
    blockAllMedia: false,
    networkDetailAllowUrls: [window.location.origin, /https:\/\/api(-test)?\.checkplease\.it.*/]
  })],
  // Performance Monitoring
  tracesSampleRate: env.SENTRY_TRACES_SAMPLE_RATE,
  tracePropagationTargets: [/https?:\/\/api(-test)?\.checkplease\.it.*/],
  // Session Replay
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1,
  // Fix handled / unhandled setting
  beforeSend: (event, _) => {
    var _event$exception, _event$exception$valu;
    var handled = event.level !== "fatal";
    (_event$exception = event.exception) === null || _event$exception === void 0 ? void 0 : (_event$exception$valu = _event$exception.values) === null || _event$exception$valu === void 0 ? void 0 : _event$exception$valu.forEach(value => {
      if (value.mechanism) {
        value.mechanism.handled = handled;
      } else {
        value.mechanism = {
          type: 'generic',
          handled: handled
        };
      }
    });
    return event;
  },
  // Source: https://gist.github.com/impressiver/5092952
  ignoreErrors: ['Failed to fetch',
  // Random plugins/extensions
  'top.GLOBALS',
  // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
  'originalCreateNotification', 'canvas.contentDocument', 'MyApp_RemoveAllHighlights', 'http://tt.epicplay.com', 'Can\'t find variable: ZiteReader', 'jigsaw is not defined', 'ComboSearch is not defined', 'http://loading.retry.widdit.com/', 'atomicFindClose',
  // Facebook borked
  'fb_xd_fragment',
  // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
  // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
  'bmi_SafeAddOnload', 'EBCallBackMessageReceived',
  // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
  'conduitPage', 'Script error.'],
  ignoreUrls: [
  // Facebook flakiness
  /graph\.facebook\.com/i,
  // Facebook blocked
  /connect\.facebook\.net\/en_US\/all\.js/i,
  // Woopra flakiness
  /eatdifferent\.com\.woopra-ns\.com/i, /static\.woopra\.com\/js\/woopra\.js/i,
  // Chrome extensions
  /extensions\//i, /^chrome:\/\//i,
  // Other plugins
  /127\.0\.0\.1:4001\/isrunning/i,
  // Cacaoweb
  /webappstoolbarba\.texthelp\.com\//i, /metrics\.itunes\.apple\.com\.edgesuite\.net\//i]
});

/**
 * The Venia adapter provides basic context objects: a router, a store, a
 * GraphQL client, and some common functions.
 */

// The Venia adapter is not opinionated about auth.
var authLink = setContext((_, _ref) => {
  var {
    headers
  } = _ref;
  // get the authentication token from local storage if it exists.

  var storage = new BrowserPersistence();
  var token = storage.getItem("signin_token");

  // return the headers to the context so httpLink can read them
  return {
    headers: _objectSpread({
      Store: env.store_view_code,
      authorization: token ? "Bearer ".concat(token) : ""
    }, headers)
  };
});
var link = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true
  },
  attempts: {
    max: 10,
    retryIf: (error, _operation) => !!error
  }
}, {
  name: "retry-all-operations"
});

// @see https://www.apollographql.com/docs/link/composition/.
var apolloLink = ApolloLink.from([new MutationQueueLink(),
// by default, RetryLink will retry an operation five (5) times.
link, authLink,
// An apollo-link-http Link
Adapter.apolloLink(apiBase)]);
ReactDOM.render( /*#__PURE__*/React.createElement(Adapter, {
  apiBase: apiBase,
  apollo: {
    link: apolloLink
  },
  store: store
}, /*#__PURE__*/React.createElement(AppContextProvider, null, /*#__PURE__*/React.createElement(App, null))), document.getElementById("root"));
registerSW();
window.addEventListener("online", () => {
  store.dispatch(app.setOnline());
});
window.addEventListener("offline", () => {
  store.dispatch(app.setOffline());
});
if (module.hot) {
  // When any of the dependencies to this entry file change we should hot reload.
  module.hot.accept();
}